import { assert } from '@deqo/foundation';
import { Arr } from '../../../CodesForFoundation';
import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { inCommands } from '../../../concepts/commands/CommandRegistry';
import { Component, ObjectManager } from '../../../concepts/objects';
import { EditorSession } from '../EditorSession';
import { RootOverride } from '../RootOverride';
import { Selection } from '../Selection';
import { AutoBringToFront } from '../entities/AutoBringToFront';
import { Phys } from '../entities/Phys';
import { Physical, RelativeTo, TransformScope } from '../entities/Physical';
import { Physicals } from '../entities/Physicals';
import { Root } from '../entities/Root';
import { SpatialIndex } from '../utilities/SpatialIndex';
import { resolvePhysicals, resolveSubjects } from './CommandUtil';


const groupByParentInOrder = (ps: Physical[]) => {
    const bag = new Map<Component, Physical[]>();
    for (const x of ps) {
        if (!bag.has(x.parent)) bag.set(x.parent, []);
        bag.get(x.parent).push(x);
    }
    for (const e of bag) {
        e[1].sort(Physicals.sorter);
    }
    return bag;
};

const isContinuous = (ps: Physical[]) => {
    for (let i = 1; i < ps.length; i++) {
        const a = ps[i - 1];
        const b = ps[i];
        if (b.order - a.order !== 1) {
            return false;
        }
    }
    return true;
};

const isContinuousAtBack = (ps: Physical[]) => {
    assert(!!ps.length, 'isContinuousAtBack: empty list?');
    // Are we in a continuous sequence?
    if (isContinuous(ps)) {
        // Are we at the front?
        const siblings = ps[0].parent.children;
        return Arr.first(ps) === Arr.first(siblings);
    }
    return false;
};

const isContinuousAtFront = (ps: Physical[]) => {
    assert(!!ps.length, 'isContinuousAtFront: empty list?');
    // Are we in a continuous sequence?
    if (isContinuous(ps)) {
        // Are we at the front?
        const siblings = ps[0].parent.children;
        return Arr.last(ps) === Arr.last(siblings);
    }
    return false;
};

// const groupOverlappingPhysicals = (ps: Physical[]) => {
//     const

export const bringForward = defineCommand(
    'bringForward',
    'Order: Bring Forward',
    (r, [targets]: [(string | string[])?]) => {

        const dispatch = r.call(CommandDispatcher);
        const objects = r.call(ObjectManager);
        const spatial = r.call(SpatialIndex);
        const subjects = Phys.from(resolveSubjects(r, targets)).orderFrontToBack();

        if (!subjects.size) return;

        objects.update(u => {
            u.rolling(subjects.key('bringForward'));

            for (const sub of subjects.takeHeads()) {

                const area = Physicals.bounds(subjects.toArray());// sub.bounds(RelativeTo.Model);
                const next = Phys.from(spatial.lookup(area))
                    .whereNotIn(subjects)
                    .whereNotDescendantOf(sub)
                    .whereAbove(sub)
                    .orderBackToFront()
                    .first();

                if (!next) continue;
                if (subjects.includes(next)) continue;

                const f = sub.createFigure();

                // If next visually "contains" the subject, then add to the start of the next.
                if (next.bounds(RelativeTo.Model).contains(area)) {
                    next.addAtBack(sub.removeSelf());
                }
                // Otherwise, add after next
                else {
                    next.addToParentAfterSelf(sub.removeSelf());

                }

                sub.setFromFigure(f);
            }

            dispatch(place, [subjects.map(x => x.id)]);
        });
    },
);

export const bringToFront = defineCommand(
    'bringToFront',
    'Order: Bring To Front',
    (r, [targets, skipPlace]: [(string | string[])?, boolean?]) => {

        const dispatch = r.call(CommandDispatcher);
        const objects = r.call(ObjectManager);
        const subjects = Phys.from(resolveSubjects(r, targets))
            .takeHeads()
            .orderBackToFront()
            .flatMap(x => [x, ...x.relations('attached', Physical)]);
        const root = objects.getRoot();

        if (!subjects.length) return;

        objects.update(() => {

            for (const sub of subjects) {
                const f = sub.createFigure();
                root.add(sub.removeSelf());
                sub.setFromFigure(f);
            }

            if (!skipPlace) {
                dispatch(place, [subjects.map(x => x.id)]);
            }

        });
    },
);

export const sendBackward = defineCommand(
    'sendBackward',
    'Order: Send Backward',
    (r, [targets]: [(string | string[])?]) => {

        const dispatch = r.call(CommandDispatcher);
        const objects = r.call(ObjectManager);
        const spatial = r.call(SpatialIndex);
        const subjects = Phys.from(resolveSubjects(r, targets)).orderBackToFront().takeHeads();
        const root = objects.getRoot();

        if (!subjects.size) return;

        objects.update(u => {
            u.rolling(`sendBackward:${subjects.map(x => x.id).join(':')}`);

            for (const sub of subjects) {

                const area = Physicals.bounds(subjects.toArray());
                const peer = Phys.from(spatial.lookup(area))
                    .whereNotIn(subjects)
                    .whereBehind(sub)
                    .whereNotAncestorOf(sub)
                    .orderFrontToBack()
                    .find(x =>
                        x.parent === root ||
                        x.parentPhysical?.bounds(RelativeTo.Model).contains(area)
                    );

                if (!peer) continue;
                if (subjects.includes(peer)) continue;

                const f = sub.createFigure();
                peer.addToParentBeforeSelf(sub.removeSelf());
                sub.setFromFigure(f);
            }

            dispatch(place, [subjects.map(x => x.id)]);
        });
    },
);

export const sendToBack = defineCommand(
    'sendToBack',
    'Order: Send To Back',
    (r, [targets]: [(string | string[])?]) => {

        const dispatch = r.call(CommandDispatcher);
        const objects = r.call(ObjectManager);
        const spatial = r.call(SpatialIndex);
        const subjects = Phys.from(resolveSubjects(r, targets)).takeHeads().orderBackToFront();
        const root = objects.getRoot();

        objects.update(u => {

            for (const sub of subjects) {

                const area = sub.bounds(RelativeTo.Model);
                const newParent = Phys.from(spatial.lookup(area))
                    .whereNotIn(subjects)
                    .whereBehind(sub)
                    .whereBoundsContain(sub)
                    .orderFrontToBack()
                    .first()
                    ?? root;

                // Find the first child that is not a subject
                const before = Phys.from(newParent.children).whereNotIn(subjects).orderBackToFront().first();

                const f = sub.createFigure();
                newParent.addBefore(before, sub.removeSelf());
                sub.setFromFigure(f);
            }

            dispatch(place, [subjects.map(x => x.id)]);
        });
    },
);

export const place = defineCommand(
    'place',
    'Place',
    (r, [targets]: [any]) => {

        const session = r.call(EditorSession);
        const objects = r.call(ObjectManager);
        const spatialIndex = r.call(SpatialIndex);

        const root = session.get(RootOverride) ?? objects.get('root', Root);
        const subjects = new Set(resolvePhysicals(r, targets));
        if (!subjects.size) return;

        const queue = Physicals.topToBottom(subjects);

        const doPlace = (subject: Physical, newParent: Physical) => {
            // This is as simple as, get the figure before remount (where it should be) and after remount (where it ends up) and
            // call applyTransformation with from as where it is and to as where it's going, and the transformation code will do
            // the rest.
            const to = subject.createFigure();
            subject.parent.remove(subject);
            newParent.add(subject);
            const from = subject.createFigure();
            const scope = new TransformScope([subject], 'poly');
            subject.applyTransformation(scope, from, to);
            objects.getActiveTriggerCycle()?.emit('@placed', subject.id);
        };

        const isPotentialParent = (subject: Physical, potentialParent: Physical) => {
            if (subject === potentialParent) return false;
            if (potentialParent.isDescendantOf(subject)) return false;
            if (Physicals.isAbove(potentialParent, subject)) return false;
            if (queue.includes(potentialParent)) return false;
            return true;
        };

        const canPlaceInto = (subject: Physical, potentialParent: Physical) => {
            if (potentialParent.boundary) return false;
            if (potentialParent.isDescendantOf(x => !!x.as(Physical)?.boundary)) return false;
            if (potentialParent === root) return false;
            if (potentialParent.is(AutoBringToFront) && potentialParent.size().equals(subject.size())) return false;
            return potentialParent.outline(RelativeTo.Model).contains(subject.outline(RelativeTo.Model));
        };

        objects.update(() => {

            while (queue.length) {
                const subject = queue.shift();
                if (subject === root) continue;

                const candidates = Array.from(spatialIndex.lookup(subject.bounds(RelativeTo.Model)))
                    .filter(c => isPotentialParent(subject, c))
                    .sort(Physicals.sorter);

                let placeInto = Arr.last(candidates);

                // If boundary, or if subject won't fit into candidate, we need to place subject "on" the candidate, instead of in...
                // so really we place into the nearest non-boundary parent
                while (placeInto && !canPlaceInto(subject, placeInto)) {
                    placeInto = placeInto.parentPhysical;
                }

                if (placeInto && subject.parent !== placeInto) {
                    doPlace(subject, placeInto);
                }
                else if (!placeInto && subject.parent !== root) {
                    doPlace(subject, root);
                }

            }
        });
    },
    []
);

export const placeSelection = defineCommand(
    'placeSelection',
    'Selection: Place',
    cc => () => {
        const session = cc.call(EditorSession);
        const selection = session.get(Selection);
        if (!selection.size) return;
        cc.dispatch(place, [selection.toArrayOfIds()]);
    }
);

export const unplace = defineCommand(
    'unplace',
    'Unplace',
    (r, [targets]: [any]) => {

        const objects = r.call(ObjectManager);

        const root = objects.get('root', Root);
        const findParentAtRoot = (s: Physical) => {
            if (!s.parentPhysical) return null;
            if (s.parentPhysical === root) return null;
            if (s.parentPhysical?.parentPhysical === root) return s.parentPhysical;
            return findParentAtRoot(s.parentPhysical);
        };

        const subjects = Phys.from(resolvePhysicals(r, targets))
            .takeHeads()
            .orderFrontToBack()
            .toArray();

        // const subjects = Physicals.topToBottom(resolveSubjects(r, targets, {}).reverse());

        objects.update(_u => {
            for (const s of subjects) {

                const oldParent = s.parentPhysical;
                const newParent = oldParent?.parentPhysical;
                if (!newParent) return;

                const figure = s.createFigure();
                newParent.addAfter(oldParent, s.removeSelf());
                s.setFromFigure(figure);
            }
        });
    },
    []
);

export const swallow = defineCommand(
    'swallow',
    'Swallow',
    (r, [targets]: [(string | string[])?]) => {

        const dispatch = r.call(CommandDispatcher);
        const objects = r.call(ObjectManager);
        const spatialIndex = r.call(SpatialIndex);
        const subjects = new Set(resolveSubjects(r, targets));

        objects.update(() => {

            const hits = new Set<Physical>();

            for (const sb of subjects) {
                const sbb = sb.bounds(RelativeTo.Model);
                for (const cch of spatialIndex.lookup(sb.bounds(RelativeTo.Model))) {
                    // If in subjects ignore
                    if (subjects.has(cch)) continue;
                    // If bounded, ignore
                    if (cch.bounded()) continue;
                    // If already ancestor ignore
                    if (cch.isDescendantOf(sb)) continue;
                    // If not "above" ignore
                    if (!Physicals.isAbove(cch, sb)) continue;
                    // If not contained fully by subject ignore
                    if (!sbb.contains(cch.bounds(RelativeTo.Model))) continue;
                    // Otherwise candidate
                    hits.add(cch);
                }
            }

            dispatch(place, [Physicals.top(hits, true).map(x => x.id)]);
        });
    },
    []
);

export const inOrderCommands = () => inCommands(
    bringForward,
    bringToFront,
    sendBackward,
    sendToBack,
    placeSelection,
    swallow,
);
